import { workerRenderRoute } from './cpuRenderRoute';

// A full-frame worker tile may omit a lower row only when its existing
// pixel-center expression produces the exact negation of the upper row.
// Both rows then use the same x coordinates and conjugate c values.
export function conjugatePartnerRow(row, height, yBase, yScale, y0) {
  const partner = height - 1 - row;
  if (partner < 0 || partner >= row || !Number.isFinite(y0)) return -1;
  const partnerY0 = yBase + (partner + 0.5) * yScale;
  return Number.isFinite(partnerY0) && Object.is(y0, -partnerY0) ? partner : -1;
}

export function hasAntipodalColumns(width, xBase, xScale) {
  if (!Number.isInteger(width) || width < 1 || !Number.isFinite(xBase) || !Number.isFinite(xScale) || xScale <= 0) {
    return false;
  }
  const half = Math.floor(width / 2);
  for (let col = 0; col <= half; col++) {
    const partner = width - 1 - col;
    const x0 = xBase + (col + 0.5) * xScale;
    if (col === partner) {
      if (!Object.is(x0, 0)) return false;
    } else {
      const partnerX0 = xBase + (partner + 0.5) * xScale;
      if (!Number.isFinite(x0) || !Number.isFinite(partnerX0) || !Object.is(x0, -partnerX0)) return false;
    }
  }
  return true;
}

export function fullConjugateRowMap({ canvasWidth: width, canvasHeight: height,
  xRange, yRange, colorParams }) {
  // Preserve the whole-count cache, period-3 worker route, and pan protocol.
  const juliaC = colorParams?.juliaC;
  if (juliaC !== undefined) {
    if (!Array.isArray(juliaC) || juliaC.length !== 2 ||
        !juliaC.every(v => typeof v === 'number' && Number.isFinite(v) && !Object.is(v, -0))) {
      return null;
    }
  }
  if (!Number.isInteger(width) || width < 1 ||
      !Number.isInteger(height) || height < 2 ||
      !Array.isArray(xRange) || !Array.isArray(yRange) ||
      xRange.length !== 2 || yRange.length !== 2 ||
      !xRange.every(Number.isFinite) || !yRange.every(Number.isFinite) ||
      workerRenderRoute(xRange, yRange, colorParams) !== 'ordinary' ||
      !(yRange[0] < 0 && yRange[1] > 0)) return null;
  const xScale = (xRange[1] - xRange[0]) / width;
  const yScale = (yRange[1] - yRange[0]) / height;
  if (!Number.isFinite(xScale) || xScale <= 0 ||
      !Number.isFinite(yScale) || yScale <= 0) return null;
  const antipodal = Boolean(juliaC && juliaC[1] !== 0);
  if (antipodal && (!(xRange[0] < 0 && xRange[1] > 0) || !hasAntipodalColumns(width, xRange[0], xScale))) {
    return null;
  }
  const partners = new Int32Array(height);
  partners.fill(-1);
  let reusedRows = 0;
  for (let row = Math.ceil(height / 2); row < height; row++) {
    const y0 = yRange[0] + (row + 0.5) * yScale;
    const partner = conjugatePartnerRow(row, height, yRange[0], yScale, y0);
    partners[row] = partner;
    reusedRows += partner >= 0;
  }
  return reusedRows ? { partners, reusedRows, ...(antipodal && { antipodal: true }) } : null;
}

export function fillConjugateRows(pixels, width, partners, antipodal = false) {
  if (antipodal) {
    const pixels32 = new Uint32Array(pixels.buffer, pixels.byteOffset, Math.floor(pixels.byteLength / 4));
    for (let row = Math.ceil(partners.length / 2); row < partners.length; row++) {
      const partner = partners[row];
      if (partner < 0) continue;
      const srcRow = partner * width;
      const dstRow = row * width;
      for (let col = 0; col < width; col++) {
        pixels32[dstRow + col] = pixels32[srcRow + width - 1 - col];
      }
    }
    return;
  }
  const bytesPerRow = width * 4;
  for (let row = Math.ceil(partners.length / 2); row < partners.length; row++) {
    const partner = partners[row];
    if (partner < 0) continue;
    const source = partner * bytesPerRow;
    pixels.set(pixels.subarray(source, source + bytesPerRow), row * bytesPerRow);
  }
}
